import React, { useState, useEffect, useCallback, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import LockIcon from "@mui/icons-material/Lock";

import { GradientSpan, H1, P2, P3 } from "./theme/text/Headings";
import {
  InsightsContainer,
  InsightsBody,
  BlurredBackground,
  CircularLockButton,
} from "./theme/QuizBox";
import { Grey, OffWhite, White08 } from "./theme/colors";

import openAIService from "../services/OpenAIService";
import { db } from "../services/firebase.config";

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  animation: ${pulse} 2s infinite;
`;

// Helper to parse HEADLINE|BODY format
// If no | separator, treat entire text as body (not gradient)
const parseInsights = (insights) => {
  if (!insights) return { headline: "", body: "", hasFormat: false };
  const parts = insights.split("|");
  const hasFormat = parts.length > 1;
  return {
    headline: hasFormat ? parts[0]?.trim() : "",
    body: hasFormat ? parts[1]?.trim() : insights,
    hasFormat,
  };
};

// Shared hook for managing insights state and logic
const useSharedInsights = ({
  quizResults,
  quizType,
  displayName,
  displayName2,
  sharedId,
  forceRegenerate,
  isCurrentUserSender = null,
}) => {
  const [person1Insights, setPerson1Insights] = useState(null);
  const [person2Insights, setPerson2Insights] = useState(null);
  const [compatibilityInsights, setCompatibilityInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const lastRegenerateTimestamp = useRef(null);
  const isRegenerating = useRef(false);

  // Determine correct names AND data for insight generation
  // Both displayName/displayName2 AND percentage1/percentage2 are relative to the viewer:
  // - percentage1 is always the CURRENT USER's data
  // - percentage2 is always the OTHER USER's data
  //
  // But insights need to be stored consistently:
  // - person1Insights = sender's insights (with sender's data)
  // - person2Insights = receiver's insights (with receiver's data)
  //
  // So we need to swap BOTH names AND data based on who is viewing

  let senderName, receiverName, senderResults, receiverResults;

  if (isCurrentUserSender === true) {
    // Current user is sender
    // percentage1 = sender's data (current user), percentage2 = receiver's data (other user)
    // Names and data are already in correct order
    senderName = displayName || "Person 1";
    receiverName = displayName2 || "Person 2";
    senderResults = quizResults.map((result) => ({
      field: result.field,
      percentage1: result.percentage1,
    }));
    receiverResults = quizResults.map((result) => ({
      field: result.field,
      percentage1: result.percentage2,
    }));
  } else if (isCurrentUserSender === false) {
    // Current user is receiver
    // percentage1 = receiver's data (current user), percentage2 = sender's data (other user)
    // Need to swap both names AND data
    senderName = displayName2 || "Person 1";
    receiverName = displayName || "Person 2";
    senderResults = quizResults.map((result) => ({
      field: result.field,
      percentage1: result.percentage2, // Sender's data is in percentage2 when receiver views
    }));
    receiverResults = quizResults.map((result) => ({
      field: result.field,
      percentage1: result.percentage1, // Receiver's data is in percentage1 when receiver views
    }));
  } else {
    // Fallback: assume names and data are in original order (sender viewing)
    senderName = displayName || "Person 1";
    receiverName = displayName2 || "Person 2";
    senderResults = quizResults.map((result) => ({
      field: result.field,
      percentage1: result.percentage1,
    }));
    receiverResults = quizResults.map((result) => ({
      field: result.field,
      percentage1: result.percentage2,
    }));
  }

  // Alias for backward compatibility with the rest of the code
  const person1Results = senderResults;
  const person2Results = receiverResults;

  // Define required fields per quiz type to ensure all data is loaded before generating insights
  const getRequiredFields = (type) => {
    switch (type) {
      case "personality-check":
        return ["feeling", "introversion", "perception", "intuition"];
      case "best-date":
        return [
          "night",
          "social",
          "planner",
          "open",
          "direct",
          "effort",
          "boujee",
        ];
      case "political-alignment":
        return [
          "socialLib",
          "econLib",
          "globalism",
          "conservation",
          "public",
          "socialSupport",
        ];
      case "attachment-styles":
        return ["anxious", "avoidant"];
      case "love-languages":
        return ["words", "acts", "gifts", "time", "touch"];
      case "am-i-gay":
        return [
          "same-sex",
          "same-gender",
          "queer-leaning",
          "at-ease",
          "queer-inclusive",
          "explorative",
        ];
      case "sex-styles":
        return ["dom", "bdsm", "voyeur", "exhibition", "casual", "rough"];
      default:
        return []; // For unknown quiz types, don't wait for specific fields
    }
  };

  // Check if all required fields are present in quiz results
  const hasAllRequiredFields = useCallback(() => {
    const requiredFields = getRequiredFields(quizType);
    if (requiredFields.length === 0) return quizResults.length > 0;

    const presentFields = quizResults.map((r) => r.field);
    return requiredFields.every((field) => presentFields.includes(field));
  }, [quizType, quizResults]);

  // Load existing insights for both people
  const loadExistingInsights = async () => {
    try {
      const insights = { person1: null, person2: null, compatibility: null };

      if (sharedId) {
        const sharedDocRef = doc(db, "shared-quizzes", sharedId);
        const sharedDoc = await getDoc(sharedDocRef);

        if (sharedDoc.exists()) {
          const sharedData = sharedDoc.data();

          // Load person1 shared insights
          if (sharedData.person1SharedInsights) {
            insights.person1 = sharedData.person1SharedInsights.content;
          }

          // Load person2 shared insights
          if (sharedData.person2SharedInsights) {
            insights.person2 = sharedData.person2SharedInsights.content;
          }

          // Load compatibility insights
          if (sharedData.compatibilityInsights) {
            insights.compatibility = sharedData.compatibilityInsights.content;
          }
        }
      }

      return insights;
    } catch (err) {
      console.warn("Could not load existing insights:", err);
      return { person1: null, person2: null, compatibility: null };
    }
  };

  // Generate all insights
  const generateAllInsights = useCallback(
    async (forceGenerate = false) => {
      console.log("🤖 SharedInsights: generateAllInsights called", {
        forceGenerate,
        person1Insights: !!person1Insights,
        person2Insights: !!person2Insights,
        compatibilityInsights: !!compatibilityInsights,
      });

      if (!quizResults || quizResults.length === 0) {
        setError("No quiz results available to analyze");
        return Promise.resolve();
      }

      setLoading(true);
      setError(null);

      try {
        const promises = [];

        // Generate individual insights for person 1 (sender)
        // Always use senderName to ensure insights are stored correctly
        if (!person1Insights || forceGenerate) {
          promises.push(
            openAIService
              .generatePersonalityInsights(person1Results, quizType, senderName)
              .then((result) => ({ type: "person1", result }))
          );
        }

        // Generate individual insights for person 2 (receiver)
        // Always use receiverName to ensure insights are stored correctly
        if (!person2Insights || forceGenerate) {
          promises.push(
            openAIService
              .generatePersonalityInsights(
                person2Results,
                quizType,
                receiverName
              )
              .then((result) => ({ type: "person2", result }))
          );
        }

        // Generate compatibility insights
        // Use sender/receiver names in consistent order
        if (!compatibilityInsights || forceGenerate) {
          promises.push(
            openAIService
              .generateCompatibilityInsights(
                quizResults,
                quizType,
                senderName,
                receiverName
              )
              .then((result) => ({ type: "compatibility", result }))
          );
        }

        const results = await Promise.all(promises);

        // Process results and save to database
        for (const { type, result } of results) {
          switch (type) {
            case "person1":
              setPerson1Insights(result.insights);
              if (sharedId) {
                const sharedDocRef = doc(db, "shared-quizzes", sharedId);
                await updateDoc(sharedDocRef, {
                  person1SharedInsights: {
                    content: result.insights,
                    generated: new Date().toISOString(),
                    quizType: quizType,
                  },
                });
              }
              break;

            case "person2":
              setPerson2Insights(result.insights);
              if (sharedId) {
                const sharedDocRef = doc(db, "shared-quizzes", sharedId);
                await updateDoc(sharedDocRef, {
                  person2SharedInsights: {
                    content: result.insights,
                    generated: new Date().toISOString(),
                    quizType: quizType,
                  },
                });
              }
              break;

            case "compatibility":
              setCompatibilityInsights(result.insights);
              if (sharedId) {
                const sharedDocRef = doc(db, "shared-quizzes", sharedId);
                await updateDoc(sharedDocRef, {
                  compatibilityInsights: {
                    content: result.insights,
                    generated: new Date().toISOString(),
                    quizType: quizType,
                  },
                });
              }
              break;

            default:
              console.warn("Unknown insight type:", type);
              break;
          }
        }
      } catch (err) {
        console.error("Error generating insights:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [
      quizResults,
      quizType,
      senderName,
      receiverName,
      sharedId,
      person1Results,
      person2Results,
      person1Insights,
      person2Insights,
      compatibilityInsights,
    ]
  );

  // Auto-generate insights when component mounts
  useEffect(() => {
    const initializeInsights = async () => {
      // Wait until all required fields are present before generating insights
      // This prevents generating insights with incomplete data (e.g., only 1 of 4 MBTI axes)
      if (
        quizResults &&
        quizResults.length > 0 &&
        sharedId &&
        !loading &&
        hasAllRequiredFields()
      ) {
        // Check for existing insights first
        const existingInsights = await loadExistingInsights();

        setPerson1Insights(existingInsights.person1);
        setPerson2Insights(existingInsights.person2);
        setCompatibilityInsights(existingInsights.compatibility);

        // Generate missing insights (only if we have a sharedId to save to)
        const needsGeneration =
          !existingInsights.person1 ||
          !existingInsights.person2 ||
          !existingInsights.compatibility;

        if (needsGeneration) {
          generateAllInsights();
        }
      }
    };

    initializeInsights();

    // eslint-disable-next-line
  }, [quizResults, sharedId, loading]);

  // Handle forced regeneration from parent
  useEffect(() => {
    if (
      forceRegenerate &&
      forceRegenerate !== lastRegenerateTimestamp.current &&
      quizResults &&
      quizResults.length > 0 &&
      sharedId &&
      !isRegenerating.current &&
      hasAllRequiredFields()
    ) {
      console.log(
        "🔄 SharedInsights: Starting forced regeneration",
        forceRegenerate
      );
      lastRegenerateTimestamp.current = forceRegenerate;
      isRegenerating.current = true;

      // Clear existing insights to force regeneration
      setPerson1Insights(null);
      setPerson2Insights(null);
      setCompatibilityInsights(null);

      // Generate new insights with a delay to ensure state is cleared
      setTimeout(() => {
        generateAllInsights(true)
          .then(() => {
            console.log("✅ SharedInsights: Regeneration completed");
          })
          .catch((error) => {
            console.error("❌ SharedInsights: Regeneration failed", error);
          })
          .finally(() => {
            isRegenerating.current = false;
          });
      }, 100);
    }
  }, [
    forceRegenerate,
    quizResults,
    sharedId,
    generateAllInsights,
    hasAllRequiredFields,
  ]);

  return {
    person1Insights,
    person2Insights,
    compatibilityInsights,
    loading,
    error,
  };
};

// Component for compatibility insights (overview)
export const CompatibilityInsights = ({
  quizResults,
  quizType,
  colors,
  displayName,
  displayName2,
  sharedId,
  forceRegenerate,
  isCurrentUserSender = null,
}) => {
  const { compatibilityInsights, loading, error } = useSharedInsights({
    quizResults,
    quizType,
    displayName,
    displayName2,
    sharedId,
    forceRegenerate,
    isCurrentUserSender,
  });

  if (loading) {
    return (
      <InsightsContainer>
        <InsightsBody>
          <LoadingContainer>
            <H1 color={colors?.background || Grey}>
              🤖 AI is analyzing your compatibility...
            </H1>
            <P3 color={Grey} style={{ marginTop: "10px" }}>
              Generating compatibility insights
            </P3>
          </LoadingContainer>
        </InsightsBody>
      </InsightsContainer>
    );
  }

  if (error) {
    return (
      <InsightsContainer>
        <InsightsBody>
          <H1 color={Grey}>Unable to generate compatibility insights</H1>
          <P3 color={Grey} style={{ marginTop: "10px" }}>
            {error}
          </P3>
        </InsightsBody>
      </InsightsContainer>
    );
  }

  if (!compatibilityInsights) {
    return (
      <InsightsContainer>
        <InsightsBody>
          <LoadingContainer>
            <H1 color={colors?.background || Grey}>
              Preparing your compatibility insights...
            </H1>
          </LoadingContainer>
        </InsightsBody>
      </InsightsContainer>
    );
  }

  // Parse HEADLINE|BODY format
  const parsed = parseInsights(compatibilityInsights);

  return (
    <InsightsContainer className="text-align-left">
      <InsightsBody>
        <P2 style={{ marginBottom: "10px", fontWeight: "900" }}>
          <GradientSpan
            $background={colors?.background}
            $transitionBackground={colors?.transitionBackground}
          >
            Compatibility Overview
          </GradientSpan>
        </P2>
        {parsed.hasFormat && parsed.headline && (
          <H1 color={OffWhite} style={{ marginBottom: "10px" }}>
            {parsed.headline}...
          </H1>
        )}
        {parsed.body && <P2 color={OffWhite}>{parsed.body}</P2>}
      </InsightsBody>
    </InsightsContainer>
  );
};

// Component for personal insights
export const PersonalInsights = ({
  quizResults,
  quizType,
  colors,
  displayName,
  displayName2,
  sharedId,
  forceRegenerate,
  isCurrentUserSender = null, // New prop to indicate if current user is the sender
  isLocked = false, // New prop to show blurred/locked state for non-Pro users
  onUnlockClick = null, // Callback for when user clicks to unlock
}) => {
  const { person1Insights, person2Insights, loading, error } =
    useSharedInsights({
      quizResults,
      quizType,
      displayName,
      displayName2,
      sharedId,
      forceRegenerate,
      isCurrentUserSender,
    });

  if (loading) {
    return (
      <InsightsContainer>
        <InsightsBody>
          <LoadingContainer>
            <H1 color={colors?.background || Grey}>
              🤖 AI is analyzing personal insights...
            </H1>
            <P3 color={Grey} style={{ marginTop: "10px" }}>
              Generating individual insights
            </P3>
          </LoadingContainer>
        </InsightsBody>
      </InsightsContainer>
    );
  }

  if (error) {
    return (
      <InsightsContainer>
        <InsightsBody>
          <H1 color={Grey}>Unable to generate personal insights</H1>
          <P3 color={Grey} style={{ marginTop: "10px" }}>
            {error}
          </P3>
        </InsightsBody>
      </InsightsContainer>
    );
  }

  const hasPersonalInsights = person1Insights || person2Insights;

  if (!hasPersonalInsights) {
    return (
      <InsightsContainer>
        <InsightsBody>
          <LoadingContainer>
            <H1 color={colors?.background || Grey}>
              Preparing your personal insights...
            </H1>
          </LoadingContainer>
        </InsightsBody>
      </InsightsContainer>
    );
  }

  // Determine which insights belong to the current user vs the other user
  // The insights are stored as:
  // - person1Insights: always contains insights for the person with percentage1 data (sender)
  // - person2Insights: always contains insights for the person with percentage2 data (receiver)
  //
  // But displayName/displayName2 change based on who is viewing:
  // - If sender is viewing: displayName = sender name, displayName2 = receiver name
  // - If receiver is viewing: displayName = receiver name, displayName2 = sender name
  //
  // We need to determine if the current user (displayName) is the sender or receiver
  // Since we don't have direct access to the sender/receiver info here, we'll use a different approach

  // The key insight: person1Insights is always for the sender, person2Insights is always for the receiver
  // We need to determine if the current user is the sender or receiver
  // We can do this by checking if the current user's name matches the sender's name

  // For now, let's implement a simpler solution:
  // Show current user's insights first, then other user's insights
  // We'll need to determine which insights belong to the current user

  // Since we don't have direct sender/receiver info in this component,
  // we'll assume that the first insights shown should always be the current user's
  // This means we need to reorder based on who is viewing

  // Let's check if we can determine this from the quiz results
  // If the current user is the sender, they should see their insights first (person1Insights)
  // If the current user is the receiver, they should see their insights first (person2Insights)

  // We need to pass additional context to determine this
  // For now, let's implement a workaround by checking if both insights exist
  // and determining the correct order based on the user's role

  // Since we can't easily determine the sender/receiver from this component alone,
  // let's modify the approach: we'll show the insights in the order that makes sense
  // for the current user, which means we need to know their role

  // The solution is to modify the component to receive additional props
  // that indicate which insights belong to the current user

  // For now, let's implement a simple fix that assumes the current user's insights
  // should be shown first, and we'll determine this by checking the content

  // Actually, let's step back and implement this correctly
  // We need to determine if the current user is the sender or receiver
  // Since person1Insights is always sender's insights and person2Insights is always receiver's insights,
  // we need to know if the current user is the sender or receiver

  // The best approach is to pass additional context from the parent component
  // But for now, let's implement a workaround:

  // We'll assume that if we have both insights, we need to show them in the right order
  // The current user should see their insights first

  // Since we can't easily determine the user's role from this component,
  // let's implement a different approach: we'll modify the parent component to pass
  // the correct insights in the right order

  // For now, let's revert to a simpler fix: we'll show both insights but in a way
  // that makes it clear which belongs to whom

  // Determine which insights belong to the current user vs the other user
  // The insights are stored as:
  // - person1Insights: always contains insights for the person with percentage1 data (sender)
  // - person2Insights: always contains insights for the person with percentage2 data (receiver)
  //
  // We need to show the current user's insights first, then the other user's insights

  let currentUserInsights, currentUserName, otherUserInsights, otherUserName;

  if (isCurrentUserSender === true) {
    // Current user is the sender, so their insights are in person1Insights
    currentUserInsights = person1Insights;
    currentUserName = displayName || "You";
    otherUserInsights = person2Insights;
    otherUserName = displayName2 || "Your Friend";
  } else if (isCurrentUserSender === false) {
    // Current user is the receiver, so their insights are in person2Insights
    currentUserInsights = person2Insights;
    currentUserName = displayName || "You";
    otherUserInsights = person1Insights;
    otherUserName = displayName2 || "Your Friend";
  } else {
    // Fallback: if we don't know the user's role, show in original order
    // This maintains backward compatibility
    currentUserInsights = person1Insights;
    currentUserName = displayName || "You";
    otherUserInsights = person2Insights;
    otherUserName = displayName2 || "Your Friend";
  }

  // Parse insights into HEADLINE|BODY format
  const currentParsed = parseInsights(currentUserInsights);
  const otherParsed = parseInsights(otherUserInsights);

  return (
    <>
      {/* Current User's Insights - Always show first */}
      {currentUserInsights && (
        <>
          {isLocked ? (
            <div style={{ position: "relative" }}>
              <BlurredBackground>
                <InsightsContainer className="text-align-left mt-10">
                  <InsightsBody>
                    <P2
                      color={White08}
                      style={{ marginBottom: "15px", fontWeight: "900" }}
                    >
                      {currentUserName}'s Insights
                    </P2>
                    {currentParsed.hasFormat && currentParsed.headline && (
                      <H1 color={OffWhite} style={{ marginBottom: "10px" }}>
                        {currentParsed.headline}...
                      </H1>
                    )}
                    {currentParsed.body && (
                      <P2 color={OffWhite}>{currentParsed.body}</P2>
                    )}
                  </InsightsBody>
                </InsightsContainer>
              </BlurredBackground>
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 10,
                }}
              >
                <CircularLockButton
                  $background={colors?.background}
                  $transitionBackground={colors?.transitionBackground}
                  onClick={onUnlockClick}
                >
                  <LockIcon style={{ fontSize: 20 }} />
                </CircularLockButton>
              </div>
            </div>
          ) : (
            <InsightsContainer className="text-align-left mt-10">
              <InsightsBody>
                <P2
                  color={White08}
                  style={{ marginBottom: "15px", fontWeight: "900" }}
                >
                  {currentUserName}'s Insights
                </P2>
                {currentParsed.hasFormat && currentParsed.headline && (
                  <H1 color={OffWhite} style={{ marginBottom: "10px" }}>
                    {currentParsed.headline}...
                  </H1>
                )}
                {currentParsed.body && (
                  <P2 color={OffWhite}>{currentParsed.body}</P2>
                )}
              </InsightsBody>
            </InsightsContainer>
          )}
        </>
      )}

      {/* Other User's Insights - Show second */}
      {otherUserInsights && (
        <>
          {isLocked ? (
            <div style={{ position: "relative" }}>
              <BlurredBackground>
                <InsightsContainer className="text-align-left mt-20">
                  <InsightsBody>
                    <P2
                      color={White08}
                      style={{ marginBottom: "15px", fontWeight: "900" }}
                    >
                      {otherUserName}'s Insights
                    </P2>
                    {otherParsed.hasFormat && otherParsed.headline && (
                      <H1 color={OffWhite} style={{ marginBottom: "10px" }}>
                        {otherParsed.headline}...
                      </H1>
                    )}
                    {otherParsed.body && (
                      <P2 color={OffWhite}>{otherParsed.body}</P2>
                    )}
                  </InsightsBody>
                </InsightsContainer>
              </BlurredBackground>
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 10,
                }}
              >
                <CircularLockButton
                  $background={colors?.background}
                  $transitionBackground={colors?.transitionBackground}
                  onClick={onUnlockClick}
                >
                  <LockIcon style={{ fontSize: 20 }} />
                </CircularLockButton>
              </div>
            </div>
          ) : (
            <InsightsContainer className="text-align-left mt-20">
              <InsightsBody>
                <P2
                  color={White08}
                  style={{ marginBottom: "15px", fontWeight: "900" }}
                >
                  {otherUserName}'s Insights
                </P2>
                {otherParsed.hasFormat && otherParsed.headline && (
                  <H1 color={OffWhite} style={{ marginBottom: "10px" }}>
                    {otherParsed.headline}...
                  </H1>
                )}
                {otherParsed.body && (
                  <P2 color={OffWhite}>{otherParsed.body}</P2>
                )}
              </InsightsBody>
            </InsightsContainer>
          )}
        </>
      )}
    </>
  );
};

// Original component maintained for backward compatibility
const SharedInsights = ({
  quizResults,
  quizType,
  colors,
  displayName,
  displayName2,
  sharedId,
  isCurrentUserSender = null,
}) => {
  const {
    person1Insights,
    person2Insights,
    compatibilityInsights,
    loading,
    error,
  } = useSharedInsights({
    quizResults,
    quizType,
    displayName,
    displayName2,
    sharedId,
    isCurrentUserSender,
  });

  if (loading) {
    return (
      <InsightsContainer>
        <InsightsBody>
          <LoadingContainer>
            <H1 color={colors?.background || Grey}>
              🤖 AI is analyzing your compatibility...
            </H1>
            <P3 color={Grey} style={{ marginTop: "10px" }}>
              Generating individual and relationship insights
            </P3>
          </LoadingContainer>
        </InsightsBody>
      </InsightsContainer>
    );
  }

  if (error) {
    return (
      <InsightsContainer>
        <InsightsBody>
          <H1 color={Grey}>Unable to generate insights at this time</H1>
          <P3 color={Grey} style={{ marginTop: "10px" }}>
            {error}
          </P3>
        </InsightsBody>
      </InsightsContainer>
    );
  }

  const hasAnyInsights =
    person1Insights || person2Insights || compatibilityInsights;

  if (!hasAnyInsights) {
    return (
      <InsightsContainer>
        <InsightsBody>
          <LoadingContainer>
            <H1 color={colors?.background || Grey}>
              Preparing your insights...
            </H1>
          </LoadingContainer>
        </InsightsBody>
      </InsightsContainer>
    );
  }

  // Parse all insights into HEADLINE|BODY format
  const compatParsed = parseInsights(compatibilityInsights);
  const person1Parsed = parseInsights(person1Insights);
  const person2Parsed = parseInsights(person2Insights);

  return (
    <>
      {/* Compatibility Insights */}
      {compatibilityInsights && (
        <InsightsContainer className="text-align-left">
          <InsightsBody>
            <P2
              color={colors?.background}
              style={{ marginBottom: "5px", fontWeight: "900" }}
            >
              Compatibility Overview
            </P2>
            {compatParsed.hasFormat && compatParsed.headline && (
              <H1 color={OffWhite} style={{ marginBottom: "10px" }}>
                {compatParsed.headline}...
              </H1>
            )}
            {compatParsed.body && <P2 color={OffWhite}>{compatParsed.body}</P2>}
          </InsightsBody>
        </InsightsContainer>
      )}

      {/* Individual Insights for Person 1 */}
      {person1Insights && (
        <InsightsContainer className="text-align-left mt-10">
          <InsightsBody>
            <P2
              color={colors?.background}
              style={{ marginBottom: "15px", fontWeight: "900" }}
            >
              {displayName || "You"}'s Insights
            </P2>
            {person1Parsed.hasFormat && person1Parsed.headline && (
              <H1 color={OffWhite} style={{ marginBottom: "10px" }}>
                {person1Parsed.headline}...
              </H1>
            )}
            {person1Parsed.body && (
              <P2 color={OffWhite}>{person1Parsed.body}</P2>
            )}
          </InsightsBody>
        </InsightsContainer>
      )}

      {/* Individual Insights for Person 2 */}
      {person2Insights && (
        <InsightsContainer className="text-align-left mt-20">
          <InsightsBody>
            <P2
              color={colors?.transitionBackground}
              style={{ marginBottom: "15px", fontWeight: "900" }}
            >
              {displayName2 || "Your Friend"}'s Insights
            </P2>
            {person2Parsed.hasFormat && person2Parsed.headline && (
              <H1 color={OffWhite} style={{ marginBottom: "10px" }}>
                {person2Parsed.headline}...
              </H1>
            )}
            {person2Parsed.body && (
              <P2 color={OffWhite}>{person2Parsed.body}</P2>
            )}
          </InsightsBody>
        </InsightsContainer>
      )}
    </>
  );
};

export default SharedInsights;
